import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import { useRecordContext } from 'react-admin';
import DecFormValidation from './DecFormValidation';

import { apiFetchResponse } from '../lib/apiFetch';

interface ValidationSummary {
  benefitsType: 'qpp' | 'cpp';
  decForm: {
    readyToSubmit: boolean;
    missingRequiredCount: number;
    missingOptionalCount: number;
    completeCount: number;
    totalCount: number;
    missingRequired: Array<{
      label: string;
      category: string;
      priority: number;
    }>;
  };
  benefitsForm: {
    readyToSubmit: boolean;
    missingRequiredCount: number;
    missingOptionalCount: number;
    completeCount: number;
    totalCount: number;
    missingRequired: Array<{
      label: string;
      category: string;
      priority: number;
    }>;
  };
}

interface FormStatusProps {
  title: string;
  formData: {
    readyToSubmit: boolean;
    missingRequiredCount: number;
    missingOptionalCount: number;
    completeCount: number;
    totalCount: number;
    missingRequired: Array<{
      label: string;
      category: string;
      priority: number;
    }>;
  };
  onClick: () => void;
  showTitle?: boolean;
  programLabel?: string;
  // When set, render as a blue hover-underline link (the row's name) with the
  // validation icon inline, instead of the icon + "Verify" label.
  linkLabel?: string;
  // Hide the inline icon in link mode (the caller shows status elsewhere).
  hideIcon?: boolean;
}

const FormStatusIndicator: React.FC<FormStatusProps> = ({ title, formData, onClick, showTitle = true, programLabel, linkLabel, hideIcon }) => {
  const getStatusColor = () => {
    if (formData.readyToSubmit && formData.missingOptionalCount === 0) {
      return '#4caf50'; // Green - fully ready
    } else if (formData.readyToSubmit) {
      return '#ff9800'; // Orange - ready but optional fields missing
    } else {
      return '#f44336'; // Red - required fields missing
    }
  };

  const getStatusIcon = () => {
    const color = getStatusColor();
    const size = 16;
    if (formData.readyToSubmit && formData.missingOptionalCount === 0) {
      return <CheckCircleIcon sx={{ color, fontSize: size }} />;
    } else if (formData.readyToSubmit) {
      return <WarningIcon sx={{ color, fontSize: size }} />;
    } else {
      return <ErrorIcon sx={{ color, fontSize: size }} />;
    }
  };

  const getTooltipText = () => {
    const suffix = programLabel ? ` (${programLabel})` : '';
    if (formData.readyToSubmit && formData.missingOptionalCount === 0) {
      return `Ready to Submit${suffix} - All fields complete`;
    } else if (formData.readyToSubmit) {
      return `Ready to Submit${suffix} - ${formData.missingOptionalCount} optional fields missing`;
    } else {
      return `Not Ready${suffix} - ${formData.missingRequiredCount} required fields missing`;
    }
  };

  if (linkLabel) {
    return (
      <Tooltip title={`${getTooltipText()}. Click to verify.`} arrow>
        <Box
          onClick={onClick}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            '&:hover .verify-link': { textDecoration: 'underline' },
          }}
        >
          <Typography
            className="verify-link"
            variant="body2"
            sx={{ color: '#2563eb', fontWeight: 500 }}
          >
            {linkLabel}
          </Typography>
          {!hideIcon && getStatusIcon()}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`${getTooltipText()}. Click for details.`} arrow>
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: 1,
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        {getStatusIcon()}
        {showTitle && (
          <Typography variant="body2" fontWeight="medium">
            {title}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

interface ValidationStatusIndicatorProps {
  formType?: 'dec' | 'benefits';
  // When set, render the row's name as a blue hover-underline link with the
  // validation icon inline (used by the funeral sidebar Status section). While
  // loading / on error / when the form doesn't apply (DEC on an Ontario file),
  // a plain non-clickable label is shown so the row never collapses.
  label?: string;
  // Suppress the inline validation icon (link mode only). Used by the Benefits
  // headline where the unified status badge already conveys completeness, so the
  // green ✓ / red error icon would be redundant.
  hideIcon?: boolean;
}

const ValidationStatusIndicator: React.FC<ValidationStatusIndicatorProps> = ({ formType, label, hideIcon }) => {
  const record = useRecordContext();
  const [validationSummary, setValidationSummary] = useState<ValidationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (record?.id) {
      void fetchValidationSummary();
    }
  }, [record?.id]);

  const fetchValidationSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetchResponse(`validation/summary/${record.id}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch validation summary: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to get validation summary');
      }

      setValidationSummary(data.data);
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching validation status');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Plain, non-clickable label used as a graceful fallback in label mode.
  const plainLabel = label ? (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
  ) : null;

  if (loading) {
    return label ? plainLabel : (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading validation status...</Typography>
      </Box>
    );
  }

  if (error) {
    return label ? plainLabel : (
      <Alert severity="error" sx={{ py: 0.5 }}>
        <Typography variant="body2">{error}</Typography>
      </Alert>
    );
  }

  if (!validationSummary) {
    return label ? plainLabel : (
      <Alert severity="warning" sx={{ py: 0.5 }}>
        <Typography variant="body2">No validation data available</Typography>
      </Alert>
    );
  }

  // If formType is specified, show only that form's indicator
  if (formType) {
    // formType 'dec' is the STATEMENT OF DEATH slot, whichever one the region
    // files: Ontario's Form 15 or Québec's Déclaration de décès. It used to be
    // hidden outright for Ontario, back when the only field list behind it was
    // the DEC's — which left Ontario staff with a dead "DEC Submitted" label
    // and no way to open the validation modal at all.
    const isOntarioCase = ((record?.region as string) || '').toLowerCase() === 'ontario';

    const formData = formType === 'dec' ? validationSummary.decForm : validationSummary.benefitsForm;
    const programLabel = formType === 'benefits'
      ? (validationSummary.benefitsType === 'cpp' ? 'CPP Benefits' : 'QPP Benefits')
      : (isOntarioCase ? 'Form 15' : 'DEC');
    const title = 'Verify';

    return (
      <>
        <FormStatusIndicator
          title={title}
          formData={formData}
          onClick={handleOpenDialog}
          showTitle={true}
          programLabel={programLabel}
          linkLabel={label}
          hideIcon={hideIcon}
        />

        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Document Field Validation</Typography>
              <IconButton onClick={handleCloseDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {record?.id && <DecFormValidation funeralId={record.id} />}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Default: show both forms side by side
  return (
    <>
      <Box display="flex" alignItems="center" gap={2}>
        <Typography variant="subtitle2" color="text.secondary">
          Document Status:
        </Typography>
        
        <FormStatusIndicator 
          title="DEC Form"
          formData={validationSummary.decForm}
          onClick={handleOpenDialog}
        />
        
        <FormStatusIndicator
          title={validationSummary.benefitsType === 'cpp' ? 'CPP Benefits' : 'QPP Benefits'}
          formData={validationSummary.benefitsForm}
          onClick={handleOpenDialog}
          programLabel={validationSummary.benefitsType === 'cpp' ? 'CPP Benefits' : 'QPP Benefits'}
        />
      </Box>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Document Field Validation</Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {record?.id && <DecFormValidation funeralId={record.id} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ValidationStatusIndicator;
